import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { CrossCloseIcon, ShowMoreIcon, CloseIcon, OpenIcon } from '@common/utils/icons/icons';
import NavigationButton from './NavigationButton';
import Typography from '@mui/material/Typography';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { Theme, useTheme } from '@mui/material';
import useMediaQuery from "@mui/material/useMediaQuery";
import Zoom from '@mui/material/Zoom';
import { navigationOpenStorageName } from './MainNavigationBar';
import { useTranslation } from 'react-i18next';
import AnnouncementBanner from './AnnouncementBanner';

type PAGE_DIVIDER_TYPE = 'DIVIDER';
export const PAGE_DIVIDER = 'DIVIDER' as PAGE_DIVIDER_TYPE;

interface Page {
  open: boolean;
  content: React.ReactNode;
  icon: React.ReactNode;
  tooltip?: string;
  title?: string | React.ReactNode;
  titleRightButton?: React.ReactNode;
  onClick: () => void;
}

const MainContentBlock: React.FC<{
  onClose?: () => void;
  isOverlayContent?: boolean;
  pages: (Page | PAGE_DIVIDER_TYPE)[];
}> = ({ onClose, isOverlayContent, pages }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const matchesSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);
  const [isToggleSideBarButtonVisible, setIsToggleSideBarButtonVisible] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(
    localStorage.getItem(navigationOpenStorageName) === 'true',
  );

  const mouseOverNavHandler = () => {
    setIsToggleSideBarButtonVisible(true);
  };

  const mouseOffNavHandler = () => {
    setIsToggleSideBarButtonVisible(false);
  };

  const navBarChangeHandler = () => {
    setIsNavOpen((prevState) => {
      localStorage.setItem(navigationOpenStorageName, (!prevState).toString());
      return !prevState;
    });
  };

  const toggleSideBarClickHandler = () => {
    setIsToggleSideBarButtonVisible(false);
    navBarChangeHandler();
  };

  const openMoreMenuHandler = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };

  const closeMoreMenuHandler = () => {
    setMoreAnchorEl(null);
  };

  return (
    <React.Fragment>
      <Stack
        direction={matchesSmDown ? 'column-reverse' : 'row'}
        sx={(theme) => ({
          height: isOverlayContent ? undefined : '100%',
          overflow: 'hidden',
          width: isOverlayContent ? undefined : '100%',
          borderRadius: isOverlayContent && !matchesSmDown ? 1 : undefined,
          position: isOverlayContent ? 'absolute' : 'relative',
          top: isOverlayContent ? 0 : undefined,
          bottom: isOverlayContent ? 0 : undefined,
          left: isOverlayContent ? 0 : undefined,
          right: isOverlayContent ? 0 : undefined,
          zIndex: theme.zIndex.fab - 1,
        })}
      >
        <Stack
          px={1}
          gap={1}
          alignItems="center"
          direction={matchesSmDown ? 'row' : 'column'}
          sx={(theme) => ({
            zIndex: theme.zIndex.fab,
            height: matchesSmDown ? theme.spacing(7) : '100%',
            flexShrink: 0,
            position: 'relative',
            backgroundColor: matchesSmDown ? theme.palette.background.paper :theme.palette.background.default,
            boxShadow: matchesSmDown ? '0 -3px 5px -1px rgba(0,0,0,0.2)' : undefined,
          })}
          onMouseEnter={mouseOverNavHandler}
          onMouseLeave={mouseOffNavHandler}
        >
          <Stack
            direction={matchesSmDown ? 'row' : 'column'}
            width="100%"
            alignItems="center"
            height="100%"
            py={matchesSmDown ? 0.5 : 1}
          >
            {matchesSmDown && pages.length > 4 ? (
              <React.Fragment>
                {pages
                  .filter((p): p is Page => p !== PAGE_DIVIDER)
                  .slice(0, 3)
                  .map((page, i) => (
                    <NavigationButton
                      key={i}
                      isNavOpen={false}
                      title={page.tooltip ?? ''}
                      icon={page.icon}
                      onClick={page.onClick}
                      isCurrentPage={page.open}
                      disableActiveBackground
                      centerAlign={matchesSmDown}
                    />
                  ))}
                <NavigationButton
                  isNavOpen={false}
                  title={t('button-more')}
                  icon={<ShowMoreIcon />}
                  onClick={openMoreMenuHandler}
                  isCurrentPage={pages
                    .filter((p): p is Page => p !== PAGE_DIVIDER)
                    .slice(3)
                    .some((page) => page.open)}
                  disableActiveBackground
                  centerAlign={matchesSmDown}
                />
                <Popover
                  open={moreAnchorEl != null}
                  anchorEl={moreAnchorEl}
                  onClose={closeMoreMenuHandler}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                  <Stack sx={{ minWidth: theme.spacing(24) }} spacing={0.5} p={0.5}>
                    {pages
                      .filter((p): p is Page => p !== PAGE_DIVIDER)
                      .slice(3)
                      .map((page, index) => (
                        <NavigationButton
                          key={index}
                          isNavOpen
                          title={page.tooltip ?? ''}
                          icon={page.icon}
                          onClick={() => {
                            page.onClick();
                            closeMoreMenuHandler();
                          }}
                          isCurrentPage={page.open}
                        />
                      ))}
                  </Stack>
                </Popover>
              </React.Fragment>
            ) : !matchesSmDown ? (
              <Box minWidth="min-content" sx={{ height: '100%', overflowY: 'auto' }}>
                <Box
                  sx={(theme) => ({
                    position: 'absolute',
                    top: theme.spacing(5.5),
                    right: theme.spacing(-2.5),
                    borderRadius: '50%',
                    zIndex: theme.zIndex.fab,
                  })}
                >
                  <Zoom in={isToggleSideBarButtonVisible} timeout={100} unmountOnExit>
                    <Fab
                      onClick={toggleSideBarClickHandler}
                      size="small"
                      sx={(theme) => ({
                        boxShadow: 2,
                        background: theme.palette.background.default,
                        ':hover': { backgroundColor: theme.palette.action.hover },
                      })}
                    >
                      {isNavOpen ? <CloseIcon /> : <OpenIcon />}
                    </Fab>
                  </Zoom>
                </Box>
                <Stack
                  data-testid="nav-bar-container"
                  sx={(theme) => ({
                    width: isNavOpen ? theme.spacing(32) : theme.spacing(7),
                    transition: 'all 0.4s ease-in-out',
                  })}
                  spacing={1}
                >
                  {onClose != null && !matchesSmDown && (
                    <NavigationButton
                      isNavOpen={isNavOpen}
                      title={t('button-close')}
                      invertColors
                      icon={<CrossCloseIcon />}
                      onClick={onClose}
                      centerAlign={matchesSmDown}
                    />
                  )}
                  {pages.map((page, index) =>
                    page === PAGE_DIVIDER ? (
                      <Divider key={index} />
                    ) : (
                      <NavigationButton
                        key={page.tooltip}
                        isNavOpen={isNavOpen}
                        title={page.tooltip ?? ''}
                        icon={page.icon}
                        onClick={page.onClick}
                        isCurrentPage={page.open}
                        centerAlign={matchesSmDown}
                      />
                    ),
                  )}
                </Stack>
              </Box>
            ) : (
              pages
                .filter((p): p is Page => p !== PAGE_DIVIDER)
                .map((page) => (
                  <NavigationButton
                    key={page.tooltip}
                    isNavOpen={isNavOpen}
                    title={page.tooltip ?? ''}
                    icon={page.icon}
                    onClick={page.onClick}
                    isCurrentPage={page.open}
                    disableActiveBackground
                    centerAlign={matchesSmDown}
                  />
                ))
            )}
          </Stack>
        </Stack>
        <Stack
          spacing={1}
          sx={{
            overflow: 'hidden',
            position: 'relative',
            height: '100%',
            flexGrow: 1,
            pr: { xs: 0, sm: 1 },
            pb: { xs: 0, sm: 1 },
          }}
        >
          {!matchesSmDown && <AnnouncementBanner />}
          <Stack
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: theme.palette.background.paper,
              borderRadius: theme.spacing(1),
            }}
          >
            {pages
              .filter((p): p is Page => p !== PAGE_DIVIDER)
              .map((page, index) =>
                page.open ? (
                  <React.Fragment key={index}>
                    {page.title != null && (
                      <Box
                        flexShrink={0}
                        sx={{
                          padding: { xs: 1, sm: 2 },
                          boxShadow: matchesSmDown ? '0 3px 5px -1px rgba(0,0,0,0.2)' : undefined,
                          zIndex: 1
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack direction="row" alignItems="center" gap={1}>
                            {matchesSmDown && onClose != null && (
                              <IconButton onClick={onClose} sx={{ ml: -1 }} aria-label="button-close">
                                <CloseIcon fontSize="large" />
                              </IconButton>
                            )}
                            {typeof page.title === 'string' ? (
                              <Typography variant="h2" lineHeight={2}>
                                {page.title}
                              </Typography>
                            ) : (
                              page.title
                            )}
                          </Stack>
                          {page.titleRightButton}
                        </Stack>
                      </Box>
                    )}
                    <Box
                      sx={{ overflowY: 'auto', padding: { xs: 1, sm: 2 } }}
                      width="100%"
                      flexGrow={1}
                    >
                      {page.content}
                    </Box>
                  </React.Fragment>
                ) : null,
              )}
          </Stack>
        </Stack>
      </Stack>
    </React.Fragment>
  );
};

export default MainContentBlock;
